import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  DollarSign, Plus, TrendingUp, TrendingDown, Wallet,
  Trash2, Edit3, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import HonorarioModal from '@/components/pericias/HonorarioModal';
import DespesaModal from '@/components/pericias/DespesaModal';

export default function FinanceiroSystemPage() {
  const { toast } = useToast();
  const [honorarios, setHonorarios] = useState([]);
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ previstos: 0, recebidos: 0, despesas: 0 });

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHonorario, setSelectedHonorario] = useState(null);
  const [despesaModalOpen, setDespesaModalOpen] = useState(false);
  const [selectedDespesa, setSelectedDespesa] = useState(null);

  useEffect(() => {
    loadFinances();
  }, []);

  const loadFinances = async () => {
    try {
      setLoading(true);
      const [hResp, dResp] = await Promise.all([
        api.honorarios.list(),
        api.despesas.list()
      ]);

      if (hResp.data) setHonorarios(hResp.data);
      if (dResp.data) setDespesas(dResp.data);

      const hData = hResp.data || [];
      const dData = dResp.data || [];

      let previstos = 0;
      let recebidos = 0;
      let totalDespesas = 0;

      hData.forEach(h => {
        const valor = parseFloat(h.valor_homologado || h.valor_proposto || 0);
        previstos += valor;
        if (h.status_pagamento === 'pago') recebidos += valor;
      });

      dData.forEach(d => {
        totalDespesas += parseFloat(d.valor || 0);
      });

      setTotals({ previstos, recebidos, despesas: totalDespesas });
    } catch (err) {
      toast({ title: "Erro ao buscar finanças", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHonorario = async (id) => {
    if (!confirm("Excluir este honorário?")) return;
    try {
      await api.honorarios.delete(id);
      toast({ title: "Sucesso", description: "Honorário excluído." });
      loadFinances();
    } catch (e) { toast({ title: "Erro", variant: "destructive" }); }
  };

  const handleDeleteDespesa = async (id) => {
    if (!confirm("Excluir esta despesa?")) return;
    try {
      await api.despesas.delete(id);
      toast({ title: "Sucesso", description: "Despesa excluída." });
      loadFinances();
    } catch (e) { toast({ title: "Erro", variant: "destructive" }); }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <Helmet>
        <title>Financeiro - PROVATIO</title>
      </Helmet>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg text-[#193D6D]">
            <DollarSign size={24} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#193D6D]">Painel Financeiro</h1>
            <p className="text-slate-500 font-medium">Fluxo de honorários e controle de despesas corporativas.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => { setSelectedDespesa(null); setDespesaModalOpen(true); }} className="text-red-500 border-red-200 hover:bg-red-50 font-semibold">
            <TrendingDown className="w-4 h-4 mr-2" /> Nova Despesa
          </Button>
          <Button onClick={() => { setSelectedHonorario(null); setModalOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            <TrendingUp className="w-4 h-4 mr-2" /> Novo Honorário
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-sm border-slate-200 bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase">Total Recebido</CardTitle>
            <Wallet className="w-4 h-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#193D6D]">{loading ? <Loader2 className="animate-spin w-5 h-5" /> : formatCurrency(totals.recebidos)}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-slate-200 bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase">Previsto / Pendente</CardTitle>
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{loading ? <Loader2 className="animate-spin w-5 h-5" /> : formatCurrency(totals.previstos)}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm border-slate-200 bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xs font-bold text-slate-500 uppercase">Despesas Totais</CardTitle>
            <TrendingDown className="w-4 h-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{loading ? <Loader2 className="animate-spin w-5 h-5" /> : formatCurrency(totals.despesas)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b bg-emerald-50/50">
            <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wider">Honorários por Processo</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Processo</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="h-32 text-center text-slate-400">Carregando...</TableCell></TableRow>
              ) : honorarios.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="h-32 text-center text-slate-400 italic">Nenhum registro.</TableCell></TableRow>
              ) : (
                honorarios.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell className="font-bold text-slate-700 text-xs">{h.processo_numero || `#${h.processo_id}`}</TableCell>
                    <TableCell className="font-medium text-slate-600">{formatCurrency(h.valor_homologado || h.valor_proposto)}</TableCell>
                    <TableCell>
                      <Badge className={h.status_pagamento === 'pago' ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-none" : "bg-amber-100 text-amber-700 hover:bg-amber-200 border-none"}>
                        {h.status_pagamento.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => { setSelectedHonorario(h); setModalOpen(true); }} className="h-8 w-8 text-blue-500"><Edit3 size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteHonorario(h.id)} className="h-8 w-8 text-red-500"><Trash2 size={14} /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b bg-red-50/50">
            <h3 className="text-sm font-bold text-red-800 uppercase tracking-wider">Despesas Operacionais</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Descrição</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="h-32 text-center text-slate-400">Carregando...</TableCell></TableRow>
              ) : despesas.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="h-32 text-center text-slate-400 italic">Nenhuma despesa.</TableCell></TableRow>
              ) : (
                despesas.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-700 text-sm">{d.descricao}</span>
                        <span className="text-[10px] text-slate-400 uppercase font-bold">{d.categoria}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(d.data_vencimento).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="font-bold text-red-600">{formatCurrency(d.valor)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => { setSelectedDespesa(d); setDespesaModalOpen(true); }} className="h-8 w-8 text-blue-500"><Edit3 size={14} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteDespesa(d.id)} className="h-8 w-8 text-red-500"><Trash2 size={14} /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {modalOpen && (
        <HonorarioModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          honorario={selectedHonorario}
          onSuccess={loadFinances}
        />
      )}
      {despesaModalOpen && (
        <DespesaModal
          open={despesaModalOpen}
          onOpenChange={setDespesaModalOpen}
          despesa={selectedDespesa}
          onSuccess={loadFinances}
        />
      )}
    </div>
  );
}